import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { toggleSelection, isAnswered, includesOther, secondaryGoalOptions, selectPrimaryGoal } from "../lib/intakeSelections";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, ShieldAlert, AlertOctagon } from "lucide-react";

const CAPITAL_BANDS = [
  "$250,000 – $1,000,000",
  "$1,000,000 – $5,000,000", "$5,000,000+", "Prefer not to say yet",
];
const PRIMARY_REASONS = [
  "Learn about capital opportunities", "Become an investor/member",
  "Become an introducer", "Explore regulated partner options",
  "Family office / strategic allocation", "Business or entity onboarding", "Other",
];
const CAPITAL_LOCATIONS = [
  "Bank account", "Fidelity", "Schwab", "E-Trade", "Robinhood",
  "Interactive Brokers", "Crypto exchange", "FX broker",
  "Business operating account", "Trust account", "Offshore account", "Other",
];
const RISK_TYPES = [
  "Risk capital", "Needed for personal expenses", "Needed for business expenses", "Mixed", "Unsure",
];
const FIN_GOALS = [
  "Capital preservation", "Education", "Diversification", "Income",
  "Growth", "Introducer participation", "Institutional access", "Other",
];
const URGENCY = [
  "I am just learning", "I want to complete verification soon",
  "I am ready to proceed if approved", "I need immediate guidance on the next step",
  "I believe my file requires priority review",
];
const PROHIBITED_STATEMENTS = [
  "Guaranteed returns", "No risk", "Safe investment", "You will make money",
  "Send funds here", "You must act fast", "You can make the same returns I made",
  "I can get you approved", "I manage the account", "I am your investment adviser",
  "Other concerning statement",
];

const RadioGroup = ({ value, onChange, options, name, testidPrefix }) => (
  <div className="space-y-2">
    {options.map((o) => (
      <label
        key={o}
        data-testid={testidPrefix ? `${testidPrefix}-${o.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}` : undefined}
        className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition ${
          value === o ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <input
          type="radio"
          name={name}
          value={o}
          checked={value === o}
          onChange={() => onChange(o)}
          className="accent-blue-600"
        />
        <span className="text-sm text-slate-900">{o}</span>
      </label>
    ))}
  </div>
);

// STARSEED-CAPMARKETS-072/073 — multi-select question (checkbox pattern
// mirrors the Introducer Statements step). Option VALUE strings must stay
// byte-identical to the engine's literals and legacy stored documents.
const CheckboxGroup = ({ values, onToggle, options, testidPrefix }) => (
  <div className="space-y-2">
    {options.map((o) => (
      <label
        key={o}
        data-testid={testidPrefix ? `${testidPrefix}-${o.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}` : undefined}
        className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition ${
          values.includes(o) ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        {/* aria-label: the Radix checkbox is a <button role="checkbox"> whose
            only content is the check indicator, so the option text must be
            attached explicitly to give it an accessible name. Row clicks
            forward to the checkbox via native <label> association (button is
            a labelable element — same shipped pattern as the Introducer
            Statements step below). */}
        <Checkbox aria-label={o} checked={values.includes(o)} onCheckedChange={() => onToggle(o)} />
        <span className="text-sm text-slate-900">{o}</span>
      </label>
    ))}
  </div>
);

const Question = ({ n, total, title, children }) => (
  <div className="mb-6">
    <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Question {n} of {total}</p>
    <h3 className="text-base font-semibold text-slate-900 mb-3">{title}</h3>
    {children}
  </div>
);

const CommandCenterIntakePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Capital Readiness
  const [cr, setCr] = useState({
    primary_reason: [],
    estimated_capital: "",
    capital_location: [],
    capital_location_other: "",
    has_emergency_fund: "",
    capital_type: "",
    financial_goal: "",
    secondary_financial_goals: [],
    urgency: "",
  });

  // Step 2: Capital Safety
  const [cs, setCs] = useState({
    understands_no_guarantee: "",
    understands_no_essential_funds: "",
    has_liquid_reserves: "",
    under_financial_pressure: "",
    promised_guaranteed_return: "",
    told_to_send_funds_outside_process: "",
  });

  // Step 3: Introducer Statements
  const [iss, setIss] = useState({ selected: [], other_note: "" });

  useEffect(() => {
    // Load existing answers if any
    api.get("/command-center/me").then((r) => {
      // No-op for now; the wizard treats each step independently and submits each on Next.
    }).catch(() => {});
  }, []);

  const submitStep1 = async () => {
    const req = ["primary_reason", "estimated_capital", "capital_location", "has_emergency_fund", "capital_type", "financial_goal", "urgency"];
    for (const k of req) if (!isAnswered(cr[k])) { toast.error(`Please answer: ${k.replace(/_/g, " ")}`); return; }
    setSubmitting(true);
    try {
      await api.post("/command-center/intake/capital-readiness", cr);
      toast.success("Capital readiness saved");
      setStep(2);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStep2 = async () => {
    const req = Object.keys(cs);
    for (const k of req) if (!cs[k]) { toast.error("Please answer all questions"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/command-center/intake/capital-safety", cs);
      if (r.data.red_alert) {
        toast.warning("Your file has been flagged for priority review.");
      } else if (r.data.compliance_attention_required) {
        toast.warning("Your answers triggered a compliance review.");
      } else {
        toast.success("Capital safety saved");
      }
      setStep(3);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStep3 = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/command-center/intake/introducer-statements", iss);
      if (r.data.red_alert) {
        toast.warning("Your file has been flagged for compliance review.");
      } else {
        toast.success("Intake complete");
      }
      navigate("/command-center");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatement = (s) => {
    setIss((prev) => ({
      ...prev,
      selected: prev.selected.includes(s)
        ? prev.selected.filter((x) => x !== s)
        : [...prev.selected, s],
    }));
  };

  return (
    <DashboardLayout title="Onboarding Intake" subtitle="Capital readiness & risk-readiness questionnaire">
      {/* Persistent do-not-send-funds */}
      <div className="mb-6 p-4 rounded-lg border-2 border-red-300 bg-red-50 flex gap-3 items-start">
        <AlertOctagon className="text-red-700 shrink-0 mt-0.5" size={20} />
        <p className="text-red-900 text-sm leading-relaxed">
          Do not send funds to EchoSense, any introducer, any individual, or any account based on verbal instructions.
          Only follow written funding instructions issued by the approved regulated partner or fund administrator
          after your file has been approved.
        </p>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50">
        <p className="text-sm text-blue-900 leading-relaxed">
          <strong>This is not investment advice.</strong> The questions below are a risk-readiness and suitability intake.
          Your answers help EchoSense determine the correct compliance pathway for your file. Some answers may flag your
          file for additional review — that is by design and protects you.
        </p>
      </div>

      <Progress value={(step / 3) * 100} className="mb-6" />

      {step === 1 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Step 1 of 3 — Capital Readiness</CardTitle>
            <CardDescription className="text-slate-600">Seven questions about your goals and capital.</CardDescription>
          </CardHeader>
          <CardContent>
            <Question n={1} total={7} title="What brings you to EchoSense? (select all that apply)">
              <CheckboxGroup testidPrefix="cr-primary" values={cr.primary_reason} onToggle={(v) => setCr({ ...cr, primary_reason: toggleSelection(cr.primary_reason, v) })} options={PRIMARY_REASONS} />
            </Question>
            <Question n={2} total={7} title="What is your estimated available investment capital?">
              <RadioGroup name="estimated_capital" testidPrefix="cr-capital" value={cr.estimated_capital} onChange={(v) => setCr({ ...cr, estimated_capital: v })} options={CAPITAL_BANDS} />
              <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-testid="cr-capital-minimum-note">
                EchoSense Capital Club is structured around a <span className="font-semibold text-slate-700">$250,000 minimum entry</span>.
                Special circumstances?{" "}
                <a href="/consultation" className="text-blue-600 hover:text-blue-700 underline underline-offset-2 font-medium" data-testid="cr-capital-special-circumstances-link">
                  Book a 30-minute private call &rarr;
                </a>
              </p>
            </Question>
            <Question n={3} total={7} title="Where is your capital currently held? (select all that apply)">
              <CheckboxGroup testidPrefix="cr-location" values={cr.capital_location} onToggle={(v) => setCr({ ...cr, capital_location: toggleSelection(cr.capital_location, v) })} options={CAPITAL_LOCATIONS} />
              {includesOther(cr.capital_location) && (
                <Input
                  data-testid="cr-location-other"
                  className="mt-2 bg-white text-slate-900 border-slate-300"
                  placeholder="Please describe"
                  value={cr.capital_location_other}
                  onChange={(e) => setCr({ ...cr, capital_location_other: e.target.value })}
                />
              )}
            </Question>
            <Question n={4} total={7} title="Do you currently have an emergency fund or liquid reserve outside of any investment capital?">
              <RadioGroup name="has_emergency_fund" testidPrefix="cr-emergency" value={cr.has_emergency_fund} onChange={(v) => setCr({ ...cr, has_emergency_fund: v })} options={["Yes", "No", "Unsure"]} />
            </Question>
            <Question n={5} total={7} title="Is the capital you are considering placing risk capital, or is it needed for living expenses, bills, payroll, debt, taxes, or emergencies?">
              <RadioGroup name="capital_type" testidPrefix="cr-type" value={cr.capital_type} onChange={(v) => setCr({ ...cr, capital_type: v })} options={RISK_TYPES} />
            </Question>
            <Question n={6} total={7} title="What is your main financial goal?">
              {/* STARSEED-CAPMARKETS-074 (option 2): primary stays a required
                  single-select; the optional secondary group below excludes the
                  current primary, and changing the primary prunes it from the
                  secondary selection so the two can never overlap. */}
              <RadioGroup name="financial_goal" testidPrefix="cr-goal" value={cr.financial_goal} onChange={(v) => setCr(selectPrimaryGoal(cr, v))} options={FIN_GOALS} />
              <p className="text-sm font-medium text-slate-900 mt-4 mb-2">Any other goals? (optional — select all that apply)</p>
              <CheckboxGroup
                testidPrefix="cr-goal-secondary"
                values={cr.secondary_financial_goals}
                onToggle={(v) => setCr({ ...cr, secondary_financial_goals: toggleSelection(cr.secondary_financial_goals, v) })}
                options={secondaryGoalOptions(FIN_GOALS, cr.financial_goal)}
              />
            </Question>
            <Question n={7} total={7} title="What level of urgency applies to your onboarding?">
              <RadioGroup name="urgency" testidPrefix="cr-urgency" value={cr.urgency} onChange={(v) => setCr({ ...cr, urgency: v })} options={URGENCY} />
            </Question>

            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => navigate("/command-center")}>Cancel</Button>
              <Button data-testid="intake-step1-next" onClick={submitStep1} disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white">
                {submitting ? "Saving..." : <>Next <ChevronRight size={16} /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Step 2 of 3 — Capital Safety</CardTitle>
            <CardDescription className="text-slate-600">Six yes/no questions about your financial situation.</CardDescription>
          </CardHeader>
          <CardContent>
            <Question n={1} total={6} title="Do you understand that no investment, trading result, allocation, account approval, or return is guaranteed?">
              <RadioGroup name="ng" testidPrefix="cs-noguarantee" value={cs.understands_no_guarantee} onChange={(v) => setCs({ ...cs, understands_no_guarantee: v })} options={["Yes", "No"]} />
            </Question>
            <Question n={2} total={6} title="Do you understand that you should not invest money needed for rent, mortgage, food, medical expenses, debt payments, payroll, taxes, or emergencies?">
              <RadioGroup name="ne" testidPrefix="cs-noessential" value={cs.understands_no_essential_funds} onChange={(v) => setCs({ ...cs, understands_no_essential_funds: v })} options={["Yes", "No"]} />
            </Question>
            <Question n={3} total={6} title="Do you have enough liquid reserves outside the capital you are considering?">
              <RadioGroup name="lr" testidPrefix="cs-reserves" value={cs.has_liquid_reserves} onChange={(v) => setCs({ ...cs, has_liquid_reserves: v })} options={["Yes", "No", "Unsure"]} />
            </Question>
            <Question n={4} total={6} title="Are you under any financial pressure to make an immediate decision?">
              <RadioGroup name="fp" testidPrefix="cs-pressure" value={cs.under_financial_pressure} onChange={(v) => setCs({ ...cs, under_financial_pressure: v })} options={["Yes", "No"]} />
            </Question>
            <Question n={5} total={6} title="Has anyone promised you a guaranteed return?">
              <RadioGroup name="gr" testidPrefix="cs-guarantee" value={cs.promised_guaranteed_return} onChange={(v) => setCs({ ...cs, promised_guaranteed_return: v })} options={["Yes", "No"]} />
            </Question>
            <Question n={6} total={6} title="Has anyone told you to send money outside the official written onboarding process?">
              <RadioGroup name="sf" testidPrefix="cs-sendfunds" value={cs.told_to_send_funds_outside_process} onChange={(v) => setCs({ ...cs, told_to_send_funds_outside_process: v })} options={["Yes", "No"]} />
            </Question>

            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft size={16} /> Back</Button>
              <Button data-testid="intake-step2-next" onClick={submitStep2} disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white">
                {submitting ? "Saving..." : <>Next <ChevronRight size={16} /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Step 3 of 3 — Introducer Statements</CardTitle>
            <CardDescription className="text-slate-600">
              If you were referred by an introducer, did anyone make any of the following statements to you? Check all that apply.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 rounded border border-amber-300 bg-amber-50">
              <p className="text-sm text-amber-900">
                <ShieldAlert size={16} className="inline mr-1" />
                Introducers are not permitted to discuss returns, guarantees, or funding instructions.
                Reporting any of these statements protects you and helps EchoSense maintain compliance.
              </p>
            </div>

            <div className="space-y-2 mb-4">
              {PROHIBITED_STATEMENTS.map((s) => (
                <label
                  key={s}
                  data-testid={`is-${s.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
                  className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition ${
                    iss.selected.includes(s) ? "border-red-400 bg-red-50" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <Checkbox checked={iss.selected.includes(s)} onCheckedChange={() => toggleStatement(s)} />
                  <span className="text-sm text-slate-900">{s}</span>
                </label>
              ))}
              <label className="flex items-center gap-3 p-3 rounded border border-slate-200 bg-white">
                <Checkbox
                  checked={iss.selected.length === 0}
                  onCheckedChange={(v) => { if (v) setIss({ ...iss, selected: [] }); }}
                />
                <span className="text-sm text-slate-900">None of the above</span>
              </label>
            </div>

            <Label className="text-slate-900">Other detail (optional)</Label>
            <Textarea
              data-testid="is-other"
              value={iss.other_note}
              onChange={(e) => setIss({ ...iss, other_note: e.target.value })}
              rows={3}
              className="mt-1 bg-white text-slate-900 border-slate-300"
              placeholder="Describe any concerning statements or interactions..."
            />

            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft size={16} /> Back</Button>
              <Button data-testid="intake-step3-submit" onClick={submitStep3} disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {submitting ? "Submitting..." : "Submit Intake"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default CommandCenterIntakePage;
